import { useQuery } from 'react-query'
import { sourcesAPI, imagesAPI, queueAPI, authorsAPI } from '../api/client'
import {
  Globe,
  Images,
  Users,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react'

function StatCard({ icon: Icon, value, label, color = 'primary' }) {
  const colorClasses = {
    primary: 'text-primary',
    success: 'text-success',
    accent: 'text-accent',
    secondary: 'text-secondary',
  }

  return (
    <div className="stat-card">
      <div className="flex items-center justify-between mb-4">
        <Icon className={`w-8 h-8 ${colorClasses[color]}`} />
      </div>
      <div className={`stat-value ${colorClasses[color]}`}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  )
}

function RecentAuthors({ authors }) {
  if (!authors?.length) {
    return (
      <div className="glass p-6">
        <h3 className="text-lg font-semibold mb-4">Авторы на проверке</h3>
        <p className="opacity-50">Авторов на проверке пока нет</p>
      </div>
    )
  }

  return (
    <div className="glass p-6">
      <h3 className="text-lg font-semibold mb-4">Авторы на проверке</h3>
      <div className="space-y-3">
        {authors.slice(0, 5).map(author => (
          <div key={author.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
            {author.avatar_url ? (
              <img src={author.avatar_url} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Users size={20} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{author.author_name || 'Без имени'}</p>
              <p className="text-sm opacity-50">{author.images_count} изображений</p>
            </div>
            <span className="badge badge-pending">Ожидает</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function QueueStatus({ status }) {
  return (
    <div className="glass p-6">
      <h3 className="text-lg font-semibold mb-4">Состояние очереди</h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="opacity-60">Воркер</span>
          <span className={`flex items-center gap-2 ${status?.is_running ? 'text-success' : 'text-accent'}`}>
            {status?.is_running ? (
              <>
                <CheckCircle size={16} />
                Работает
              </>
            ) : (
              <>
                <AlertCircle size={16} />
                Остановлен
              </>
            )}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="opacity-60">Ожидает</span>
          <span className="font-medium">{status?.pending_count || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="opacity-60">Обработано сегодня</span>
          <span className="font-medium">{status?.processed_today || 0}</span>
        </div>
        {status?.current_task && (
          <div className="mt-4 p-3 bg-white/5 rounded-lg">
            <p className="text-sm opacity-60">Текущая задача</p>
            <p className="font-medium">{status.current_task.task_type}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function RecentImages({ images }) {
  if (!images?.length) {
    return (
      <div className="glass p-6">
        <h3 className="text-lg font-semibold mb-4">Последние изображения</h3>
        <p className="opacity-50">Изображений пока нет</p>
      </div>
    )
  }

  return (
    <div className="glass p-6">
      <h3 className="text-lg font-semibold mb-4">Последние изображения</h3>
      <div className="image-grid">
        {images.slice(0, 12).map(image => (
          <div key={image.id} className="image-card">
            <img
              src={image.thumbnail_url || image.image_url}
              alt={image.title || ''}
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { data: sourceStats } = useQuery('sourceStats', sourcesAPI.getStats)
  const { data: imageStats } = useQuery('imageStats', imagesAPI.getStats)
  const { data: queueStatus } = useQuery('queueStatus', queueAPI.getStatus, {
    refetchInterval: 5000 // Refresh every 5 seconds
  })
  const { data: pendingAuthors } = useQuery('pendingAuthors', () => authorsAPI.getPending(5))
  const { data: recentImages } = useQuery('recentImages', () => imagesAPI.getAll({ limit: 12 }))

  const totalSources = sourceStats?.length || 0
  const totalAuthors = sourceStats?.reduce((sum, s) => sum + s.total_authors, 0) || 0
  const approvedAuthors = sourceStats?.reduce((sum, s) => sum + s.approved_authors, 0) || 0
  const totalImages = imageStats?.total || 0
  const publishedImages = imageStats?.published || 0

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Панель</h1>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Globe}
          value={totalSources}
          label="Источники"
          color="primary"
        />
        <StatCard
          icon={Users}
          value={`${approvedAuthors}/${totalAuthors}`}
          label="Одобренные авторы"
          color="success"
        />
        <StatCard
          icon={Images}
          value={totalImages}
          label="Всего изображений"
          color="secondary"
        />
        <StatCard
          icon={CheckCircle}
          value={publishedImages}
          label="Опубликовано"
          color="accent"
        />
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentAuthors authors={pendingAuthors} />
        <QueueStatus status={queueStatus} />
        <div className="lg:col-span-2">
          <RecentImages images={recentImages} />
        </div>
      </div>
    </div>
  )
}
